import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Filter, Hammer, Eye } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast } from '../../../components/Toast'
import { CommessaList, StatoCommessa } from '../../../types/commesse'

const statoBadge: Record<StatoCommessa, { label: string; bg: string; text: string }> = {
  pianificazione: { label: 'Pianificazione', bg: 'bg-gray-100', text: 'text-gray-700' },
  in_revisione: { label: 'In Revisione', bg: 'bg-purple-100', text: 'text-purple-700' },
  approvata: { label: 'Approvata', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  in_esecuzione: { label: 'In Esecuzione', bg: 'bg-blue-100', text: 'text-blue-700' },
  completata: { label: 'Completata', bg: 'bg-green-100', text: 'text-green-700' },
  sospesa: { label: 'Sospesa', bg: 'bg-amber-100', text: 'text-amber-700' },
  rifiutata: { label: 'Rifiutata', bg: 'bg-red-100', text: 'text-red-700' },
}

export default function CommessePage() {
  const navigate = useNavigate()
  const [commesse, setCommesse] = useState<CommessaList[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStato, setFilterStato] = useState<StatoCommessa | ''>('')
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const res = await api.get('/v1/commesse')
      setCommesse(res.data)
    } catch {
      setToast({ message: 'Errore nel caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('it-IT')
  }

  const filtered = commesse.filter(c => {
    const matchSearch = !search ||
      c.titolo.toLowerCase().includes(search.toLowerCase()) ||
      c.numero_commessa.toLowerCase().includes(search.toLowerCase()) ||
      c.cliente_nome.toLowerCase().includes(search.toLowerCase())
    const matchStato = !filterStato || c.stato === filterStato
    return matchSearch && matchStato
  })

  return (
    <div>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commesse</h1>
          <p className="text-gray-500">Gestione ordini esecutivi</p>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca commesse..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={filterStato}
              onChange={(e) => setFilterStato(e.target.value as StatoCommessa | '')}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none bg-white"
            >
              <option value="">Tutti gli stati</option>
              <option value="pianificazione">Pianificazione</option>
              <option value="in_esecuzione">In Esecuzione</option>
              <option value="completata">Completata</option>
              <option value="sospesa">Sospesa</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabella */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Hammer className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            {search || filterStato
              ? 'Nessuna commessa trovata con i filtri selezionati'
              : 'Nessuna commessa presente. Le commesse vengono create dalle offerte confermate.'}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Numero</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titolo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Responsabile</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consegna</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Avanzamento</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((c) => {
                const badge = statoBadge[c.stato]
                const progressPerc = c.fasi_totali > 0
                  ? Math.round((c.fasi_completate / c.fasi_totali) * 100)
                  : 0

                return (
                  <tr
                    key={c.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/app/commesse/${c.id}`)}
                  >
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                        {c.numero_commessa}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{c.titolo}</div>
                      <div className="text-xs text-gray-400">
                        {c.giorni_svolti}/{c.giorni_previsti} giorni
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{c.cliente_nome}</td>
                    <td className="px-6 py-4 text-gray-600">{c.responsabile_nome}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {formatDate(c.data_prevista_consegna)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              progressPerc === 100 ? 'bg-green-500' : 'bg-blue-500'
                            }`}
                            style={{ width: `${progressPerc}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 w-12 text-right">
                          {c.fasi_completate}/{c.fasi_totali}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => navigate(`/app/commesse/${c.id}`)}
                        className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                        title="Visualizza"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
